import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Description as DocumentIcon,
  Security as SecurityIcon,
  AccountBalance as CreditIcon
} from '@mui/icons-material';
import autograbService from '../services/autograb.service';
import { CreditsService } from '../services/credits.service';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

interface PPSRCheckProps {
  vehicleId?: string;
  registration: string;
  state: string;
  vin?: string;
  onResultUpdate?: (result: any) => void;
  showCost?: boolean;
}

export default function PPSRCheck({
  vehicleId,
  registration,
  state,
  vin,
  onResultUpdate,
  showCost = false
}: PPSRCheckProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [insufficientCreditsDialog, setInsufficientCreditsDialog] = useState(false);
  const [checkingCredits, setCheckingCredits] = useState(false);

  const ppsrCost = CreditsService.SERVICE_COSTS.PPSR_CHECK;

  useEffect(() => {
    if (user?.organization_id) {
      loadCreditBalance();
    }
  }, [user?.organization_id]);

  const loadCreditBalance = async () => {
    if (!user?.organization_id) return;

    try {
      const credits = await CreditsService.getOrganizationCredits(user.organization_id);
      setCreditBalance(credits?.credit_balance || 0);
    } catch (err) {
      console.error('Error loading credit balance:', err);
    }
  };

  const runPPSRCheck = async () => {
    // Enhanced validation
    if (!registration || registration.trim() === '') {
      setError('Vehicle registration number is required for PPSR check');
      return;
    }

    if (!state || state.trim() === '') {
      setError('Vehicle state is required for PPSR check');
      return;
    }

    // Validate Australian states
    const validStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
    if (!validStates.includes(state.toUpperCase())) {
      setError(`Invalid state "${state}". Must be one of: ${validStates.join(', ')}`);
      return;
    }

    // Validate registration format (basic check)
    const cleanRego = registration.trim().toUpperCase();
    if (cleanRego.length < 3 || cleanRego.length > 8) {
      setError('Registration number must be between 3-8 characters');
      return;
    }

    // Check if user and organization are available
    if (!user?.organization_id || !user?.id) {
      setError('User authentication required for PPSR check');
      return;
    }

    // Check credit balance first
    setCheckingCredits(true);
    try {
      const hasSufficientCredits = await CreditsService.checkSufficientCredits(
        user.organization_id,
        ppsrCost
      );

      if (!hasSufficientCredits) {
        setInsufficientCreditsDialog(true);
        return;
      }
    } catch (err) {
      console.error('Error checking credits:', err);
      setError('Failed to verify credit balance. Please try again.');
      return;
    } finally {
      setCheckingCredits(false);
    }

    setLoading(true);
    setError('');

    try {
      console.log(`Starting PPSR check for: ${cleanRego} (${state.toUpperCase()})`);

      const ppsrResult = await autograbService.getPPSRReport(cleanRego, state.toUpperCase());

      console.log('PPSR Result received:', ppsrResult);

      if (ppsrResult?.certificate) {
        // Save PPSR certificate to database first
        const certificateData = {
          organization_id: user.organization_id,
          vehicle_id: vehicleId,
          registration: cleanRego,
          state: state.toUpperCase(),
          vin: vin,
          certificate_number: ppsrResult.certificate.certificate_number,
          search_number: ppsrResult.certificate.search_number,
          type: 'ppsr',
          has_secured_parties: ppsrResult.certificate.has_secured_parties || false,
          has_stolen_records: ppsrResult.certificate.has_stolen_records || false,
          has_written_off_records: ppsrResult.certificate.has_written_off_records || false,
          has_safety_recalls: ppsrResult.certificate.has_safety_recalls || false,
          vehicle_details: {
            year: ppsrResult.certificate.year,
            make: ppsrResult.certificate.make,
            model: ppsrResult.certificate.model,
            body_type: ppsrResult.certificate.body_type,
            colour: ppsrResult.certificate.colour,
            engine_number: ppsrResult.certificate.engine_number,
          },
          has_takata_recall: ppsrResult.certificate.has_takata_recall || false,
          takata_airbag_replaced: false,
          report_url: ppsrResult.certificate.url,
          autograb_certificate_id: ppsrResult.certificate.id,
          cost: CreditsService.SERVICE_COSTS.PPSR_CHECK,
          checked_at: new Date().toISOString(),
          created_by: user.id,
        };

        const { data: savedCertificate, error: saveError } = await supabase
          .from('ppsr_certificates')
          .insert([certificateData])
          .select()
          .single();

        if (saveError) {
          console.error('Error saving PPSR certificate:', saveError);
          setError('PPSR check completed but failed to save results');
          return;
        }

        console.log('PPSR certificate saved successfully:', savedCertificate);

        // Now deduct credits using the saved certificate ID
        const creditResult = await CreditsService.processCheckCredits(
          user.organization_id,
          user.id,
          'ppsr_check',
          savedCertificate.id
        );

        if (creditResult.success) {
          // Update local credit balance
          await loadCreditBalance();
        } else {
          console.warn('Credit deduction failed:', creditResult.message);
        }

        setResult(ppsrResult.certificate);

        // Notify parent component with the saved certificate ID
        if (onResultUpdate) {
          onResultUpdate({
            hasEncumbrance: ppsrResult.certificate.has_secured_parties,
            stolenStatus: ppsrResult.certificate.has_stolen_records,
            writeOffStatus: ppsrResult.certificate.has_written_off_records,
            safetyRecalls: ppsrResult.certificate.has_safety_recalls,
            reportUrl: ppsrResult.certificate.url,
            certificateId: savedCertificate.id // Use our saved certificate ID
          });
        }
      } else if (ppsrResult?.error) {
        // Handle API errors more gracefully
        setError(`PPSR Check failed: ${ppsrResult.error}`);
      } else {
        setError('PPSR check completed but no certificate data was returned');
      }
    } catch (err: any) {
      console.error('PPSR Check failed with error:', err);

      // Handle different types of errors
      let errorMessage = 'Failed to run PPSR check';

      if (err.status === 400) {
        errorMessage = `Request validation failed: ${err.message || 'Invalid registration or state parameter'}`;
      } else if (err.status === 401) {
        errorMessage = 'API authentication failed - please check AutoGrab API key configuration';
      } else if (err.status === 403) {
        errorMessage = 'Access denied - insufficient API permissions';
      } else if (err.status === 404) {
        errorMessage = `Vehicle not found: Registration "${cleanRego}" not found in ${state.toUpperCase()}`;
      } else if (err.status === 429) {
        errorMessage = 'Rate limit exceeded - please wait before trying again';
      } else if (err.status >= 500) {
        errorMessage = 'AutoGrab service is temporarily unavailable - please try again later';
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Add request details for debugging
      if (err.requestPayload) {
        console.error('Request payload:', err.requestPayload);
        errorMessage += ` (Request: ${JSON.stringify(err.requestPayload)})`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (hasIssue: boolean) => {
    return hasIssue ? 'error' : 'success';
  };

  const getStatusIcon = (hasIssue: boolean) => {
    return hasIssue ? <CancelIcon /> : <CheckIcon />;
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6">
                PPSR Compliance Check
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {showCost && (
                <Chip
                  label={`Cost: ${ppsrCost} credits`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              <Chip
                icon={<CreditIcon />}
                label={`Balance: ${creditBalance.toFixed(1)}`}
                size="small"
                color={creditBalance >= ppsrCost ? 'success' : 'error'}
                variant="outlined"
              />
            </Stack>
          </Box>

          {registration ? (
            <Typography variant="body2" color="text.secondary">
              Personal Property Securities Register check for: <strong>{registration} ({state})</strong>
            </Typography>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Registration number is required for PPSR check. Please ensure the vehicle has a valid registration number and state.
              </Typography>
            </Alert>
          )}

          {!result && (
            <Button
              variant="contained"
              onClick={runPPSRCheck}
              disabled={loading || checkingCredits || creditBalance < ppsrCost}
              startIcon={
                loading || checkingCredits ? (
                  <CircularProgress size={20} />
                ) : (
                  <SecurityIcon />
                )
              }
            >
              {loading
                ? 'Running Check...'
                : checkingCredits
                ? 'Checking Credits...'
                : creditBalance < ppsrCost
                ? 'Insufficient Credits'
                : 'Run PPSR Check'}
            </Button>
          )}

          {creditBalance < ppsrCost && !result && (
            <Alert severity="warning">
              <Typography variant="body2">
                Insufficient credits. You need {ppsrCost} credits to run a PPSR check.
                Current balance: {creditBalance.toFixed(1)} credits.
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {result && (
            <>
              <Alert 
                severity={
                  result.has_secured_parties || 
                  result.has_stolen_records || 
                  result.has_written_off_records 
                    ? 'error' 
                    : 'success'
                }
              >
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {result.has_secured_parties || 
                   result.has_stolen_records || 
                   result.has_written_off_records 
                    ? '⚠️ Vehicle has issues that must be resolved'
                    : '✅ Vehicle is clear for sale'}
                </Typography>
              </Alert>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(result.has_secured_parties)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Finance Check"
                    secondary={result.has_secured_parties ? 'Has secured parties/finance owing' : 'No finance owing'}
                  />
                  <Chip 
                    label={result.has_secured_parties ? 'FAIL' : 'PASS'}
                    color={getStatusColor(result.has_secured_parties)}
                    size="small"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(result.has_stolen_records)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Stolen Check"
                    secondary={result.has_stolen_records ? 'Vehicle reported stolen' : 'Not reported stolen'}
                  />
                  <Chip 
                    label={result.has_stolen_records ? 'FAIL' : 'PASS'}
                    color={getStatusColor(result.has_stolen_records)}
                    size="small"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(result.has_written_off_records)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Written Off Check"
                    secondary={result.has_written_off_records ? 'Vehicle has been written off' : 'Not written off'}
                  />
                  <Chip 
                    label={result.has_written_off_records ? 'FAIL' : 'PASS'}
                    color={getStatusColor(result.has_written_off_records)}
                    size="small"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(result.has_safety_recalls)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Safety Recalls"
                    secondary={result.has_safety_recalls ? 'Has active safety recalls' : 'No safety recalls'}
                  />
                  <Chip 
                    label={result.has_safety_recalls ? 'WARNING' : 'PASS'}
                    color={result.has_safety_recalls ? 'warning' : 'success'}
                    size="small"
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<DocumentIcon />}
                  component={Link}
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Full Report (PDF)
                </Button>
                
                <Typography variant="caption" color="text.secondary">
                  Certificate ID: {result.id}
                </Typography>
              </Box>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Vehicle Details: {result.year || '---'} {result.make} {result.model} • 
                  {result.body_type} • {result.colour} • VIN: {result.vin || vin || 'Unknown'}
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>

      {/* Insufficient Credits Dialog */}
      <Dialog
        open={insufficientCreditsDialog}
        onClose={() => setInsufficientCreditsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insufficient Credits</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You don't have enough credits to run a PPSR check.
            </Typography>
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Required:</strong> {ppsrCost} credits
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Current Balance:</strong> {creditBalance.toFixed(1)} credits
            </Typography>
            <Typography variant="body2" color="error">
              <strong>Needed:</strong> {(ppsrCost - creditBalance).toFixed(1)} more credits
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Please add more credits to your organization account to continue with PPSR checks.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsufficientCreditsDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setInsufficientCreditsDialog(false);
              // Navigate to credits page - you may want to use React Router here
              window.open('/credits', '_blank');
            }}
          >
            Add Credits
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}